import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAtomValue } from 'jotai';
import { MaggieHttp } from '../../../services/MaggieHttp';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { portcosAtomJ } from '../../../services/state/PortCosStateJ';
import { plainDateFromDateOrDateString } from '../../../util/date-utilts';

interface ExportFormData {
  fundIds: number[];
  companyIds: number[];
  asOfDate?: string;
}

// FD-2552: Sentry flagged a 400 on /metrics/cashflows where `companyIds` contained a
// value that stringified to "" (Sentry rendered it as `["companyIds", null]`). The
// companyRecord.id column is a NOT NULL int PK — verified against monashees — so the
// bad value is introduced somewhere between the API response and this call. Capture
// any offenders with full context the next time it fires so we can pinpoint the source,
// and skip them so the user's export still succeeds.
//
// Emits one consolidated Sentry event per call (not one per bad id) to avoid flooding
// Sentry if the upstream bug produces many bad entries at once.
function appendNumericIds(params: URLSearchParams, key: string, ids: readonly unknown[]): void {
  const invalid: Array<{ index: number; value: unknown; valueType: string; valueJson: string }> = [];

  ids.forEach((id, index) => {
    if (typeof id !== 'number' || !Number.isFinite(id)) {
      invalid.push({ index, value: id, valueType: typeof id, valueJson: safeJsonStringify(id) });
      return;
    }
    params.append(key, id.toString());
  });

  if (invalid.length > 0) {
    Sentry.captureMessage('cashflows-export: non-numeric ids skipped', {
      level: 'warning',
      extra: {
        paramKey: key,
        invalidCount: invalid.length,
        invalidEntries: invalid,
        allIds: safeJsonStringify(ids),
      },
    });
  }
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export async function fetchCashFlowCsv({
  companyIds,
  fundIds,
  asOfDate,
}: {
  companyIds: number[];
  fundIds: number[];
  asOfDate?: string;
}): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();

  appendNumericIds(params, 'companyIds', companyIds);
  appendNumericIds(params, 'fundIds', fundIds);

  if (asOfDate) {
    params.append('asOfDate', plainDateFromDateOrDateString(asOfDate));
  }

  const response = await MaggieHttp.maggie.get<Blob>(`/metrics/cashflows?${params.toString()}`, {
    responseType: 'blob',
  });

  // Extract filename from Content-Disposition header
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'cashflows.csv';

  return { blob: response.data, filename };
}

export function useCashFlowsExport() {
  const [isExporting, setIsExporting] = useState(false);
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();
  const portCos = useAtomValue(portcosAtomJ);

  const exportCashFlows = useCallback(
    async (formData: ExportFormData) => {
      setIsExporting(true);

      try {
        if (portCos.length === 0) {
          throw new Error('Companies data not available');
        }

        // Make single API call with arrays
        const { blob, filename } = await fetchCashFlowCsv({
          companyIds: formData.companyIds,
          fundIds: formData.fundIds,
          asOfDate: formData.asOfDate,
        });

        // Download the CSV file
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        pushSuccessToast({ message: 'Cash flows exported successfully' });
      } catch (error) {
        console.error('Export failed:', error);
        pushErrorToast({
          message: error instanceof Error ? error.message : 'Failed to export cash flows',
        });
      } finally {
        setIsExporting(false);
      }
    },
    [portCos, pushSuccessToast, pushErrorToast]
  );

  return { exportCashFlows, isExporting };
}
